import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, FormControl } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EmailIcon from '@mui/icons-material/Email';

import Page from "~/components/Page";
import AdminCreateProjectModal from "~/modals/AdminCreateProjectModal";
import AdminProjectsTable from "~/tables/AdminProjectsTable";
import { useTrans } from "~/hooks/useTrans";
import { apiGetAdminProjects } from "~/providers/api";
import TableControls from '~/components/TableControls';
import { IAdminProject, IAdminSearchProject, IAdminSearchProjectFilter } from "~/providers/admin-interfaces";
import ErrorInfo from '~/components/ErrorInfo';
import { myUseState } from "~/hooks/myUseState";
import { getLink } from "~/providers/route";
import { useLang } from "~/hooks/useLang";
import { ADMIN_PROJECTS_FILTER_NAMING, DEFAULT_BUSSINESS_PLAN_TYPE_FILTER, PAGE_SIZE_ADMIN_PROJECTS, ADMIN_PROJECTS_SORT_KEYS } from "~/const";
import MyPagination from "~/mui/MyPagination";
import AdminProjectsFilter from "~/components/AdminProjectsFilter";
import { projectsFilterStore } from "~/stores/projectsFilter";
import AdminSendBulkEmailModal from "~/modals/AdminSendBulkEmailModal";

import "./style.less";

interface IState {
	loaded: boolean;
	loading: boolean;
	isDirty: boolean;
	projects: Array<IAdminSearchProject>;
	errorMsg: string;
	createProject: boolean;
	goToProject: number;
	projectsCount: number;
	filterData: null | IAdminSearchProjectFilter;
	bulkEmail: boolean;
	projectIds: Array<number>;
}

const INIT_STATE: IState = {
	loaded: false,
	loading: true,
	isDirty: false,
	projects: [],
	errorMsg: "",
	createProject: false,
	goToProject: 0,
	projectsCount: 0,
	filterData: null,
	bulkEmail: false,
	projectIds: [],
};

const INIT_STATE_FILTER: IAdminSearchProjectFilter = {
	businessPlanType: DEFAULT_BUSSINESS_PLAN_TYPE_FILTER,
	offset: 0,
	limit: PAGE_SIZE_ADMIN_PROJECTS,
};

export default function AdminProjectsPage() {
	const navigate = useNavigate();
	const { trans, filterSearchProjectsDesc } = useTrans();
	const { lang } = useLang();
	const { state, updateState } = myUseState<IState>(INIT_STATE);

	function getSelectedId() {
		const usp = new URLSearchParams(location.search);
		const id = usp.get("id");

		return id
			? parseFloat(id)
			: -1;
	}

	function urlToName(name: string) {
		const filtered = ADMIN_PROJECTS_FILTER_NAMING.filter(item => item[1] === name);

		return filtered.length === 1
			? filtered[0][0]
			: "";
	}

	const { state: stateFilter, setState: setStateFilter, updateState: updateStateFilter } = myUseState<IAdminSearchProjectFilter>({
		...INIT_STATE_FILTER,
	});
	const selectedId = getSelectedId();

	function goToProjectId(projectId: number) {
		// prejdeme na detail projektu
		navigate(getLink("project-dashboard", true, { projectId }));
	}

	function onProjectCreate(projectData: IAdminProject) {
		updateState({
			isDirty: false,
			createProject: false,
			goToProject: projectData.id,
		});
	}

	async function loadData(filter: IAdminSearchProjectFilter = stateFilter) {
		updateState({
			loading: true,
			projects: [],
			errorMsg: "",
			projectsCount: 0,
			projectIds: [],
		});

		const projectsData = await apiGetAdminProjects(filter, lang);

		updateState({
			loading: false,
			...projectsData.noErrors
				? {
					projects: projectsData.data,
					projectsCount: projectsData.requestData.pagination.total,
					projectIds: projectsData.requestData.allProjectIds,
				}
				: {},
			errorMsg: projectsData.errors.join(", "),
		});
		projectsData.noErrors && updateStateFilter({
			offset: projectsData.requestData.pagination.offset,
		});
	}

	function nameToUrl(name: keyof IAdminSearchProjectFilter) {
		const filtered = ADMIN_PROJECTS_FILTER_NAMING.filter(item => item[0] === name);

		return filtered.length === 1
			? filtered[0][1]
			: "";
	}

	function getFilterUrl(filter: IAdminSearchProjectFilter, preserveId?: boolean) {
		const curUrl = new URL(location.href.replace(/[?].*$/u, ""));

		// propagace id
		if (preserveId) {
			const usp = new URLSearchParams(location.search);
			const id = usp.get("id");

			id && curUrl.searchParams.append("id", id);
		}

		Object.keys(filter).forEach((key: keyof IAdminSearchProjectFilter) => {
			const value = filter[key];

			if (key === "limit" || (typeof value === "string" && !value) || (key === "offset" && value === 0)) {
				return;
			}

			curUrl.searchParams.append(nameToUrl(key), value.toString());
		});

		return curUrl.toString();
	}

	function setFilterAndLoadData(newFilter: IAdminSearchProjectFilter) {
		setStateFilter(newFilter);
		loadData(newFilter);
		projectsFilterStore.getState().setFilter(newFilter);

		const newUrl = getFilterUrl(newFilter);

		history.replaceState({}, "", newUrl);
	}

	function onTableSortUpdate(newFilter: IAdminSearchProjectFilter) {
		const curFilter: IAdminSearchProjectFilter = {
			...stateFilter,
			offset: 0,
		};

		ADMIN_PROJECTS_SORT_KEYS.forEach(sort => {
			if (sort in curFilter) {
				delete curFilter[sort];
			}
		});

		setFilterAndLoadData({
			...curFilter,
			...newFilter,
		});
	}

	function onPageChange(page: number) {
		const offset = (page - 1) * PAGE_SIZE_ADMIN_PROJECTS;

		setFilterAndLoadData({
			...stateFilter,
			offset,
		});
	}

	function onPageLoad(isAuthorized: boolean) {
		if (isAuthorized) {
			updateState({
				...INIT_STATE,
				loaded: true,
			});
		}
	}

	function toggleFilters() {
		const filterData = state.filterData
			? null
			: {
				...stateFilter,
			};

		updateState({
			filterData,
		});
	}

	function clearFilters() {
		updateState({
			filterData: null,
		});
		setFilterAndLoadData({
			...INIT_STATE_FILTER,
		});
	}

	function onSetFilters(newFilter: IAdminSearchProjectFilter) {
		updateState({
			filterData: null,
		});
		setFilterAndLoadData(newFilter);
	}

	function hasFilters() {
		return Object.keys(stateFilter).length > Object.keys(INIT_STATE_FILTER).length;
	}

	useEffect(() => {
		if (!state.isDirty && state.goToProject > 0) {
			goToProjectId(state.goToProject);
		}
	}, [state.isDirty, state.goToProject]);

	useEffect(() => {
		if (state.loaded) {
			const filtersState = projectsFilterStore.getState();
			const usp = new URLSearchParams(location.search);
			const searchFilter: IAdminSearchProjectFilter = {};

			usp.forEach((value, key) => {
				const filterName = urlToName(key);

				if (filterName) {
					let newValue: any = value;

					if (value === "true") {
						newValue = true;
					} else if (value === "false") {
						newValue = false;
					} else if (filterName === "offset") {
						newValue = parseFloat(value);
					}

					// @ts-ignore
					searchFilter[filterName] = newValue;
				}
			});

			let curFilter: IAdminSearchProjectFilter = {};

			// pouzijeme url
			if (Object.keys(searchFilter).length > 0) {
				const updSearchFilter = {
					...filtersState.data.wasSet
						? filtersState.data.filter
						: INIT_STATE_FILTER,
					...searchFilter,
				};

				// nastavime z url
				filtersState.setFilter(updSearchFilter);
				curFilter = updSearchFilter;
			// z pameti - upravime url
			} else if (filtersState.data.wasSet) {
				const newUrl = getFilterUrl(filtersState.data.filter, true);

				curFilter = filtersState.data.filter;
				history.replaceState({}, "", newUrl);
			// default
			} else {
				curFilter = INIT_STATE_FILTER;
			}

			setFilterAndLoadData(curFilter);
		}
	}, [state.loaded]);

	return <Page title={trans("menu.adminProjects")} breadcrumbs={[trans("administration"), trans("projects")]} checkInternalRole="salesman" onLoad={onPageLoad} isDirty={state.isDirty} seoTitleTrans="seo.title.admin.projects">
		<div className="adminProjectsPage__addForm">
			<FormControl size="small">
				<Button color="success" variant="contained" onClick={() => updateState({ bulkEmail: true })} className="adminProjectsPage__preBtn btn-with-icon" disabled={state.projectIds.length === 0}>
					<EmailIcon /> { trans("bulkEmailModal.title") }
				</Button>
			</FormControl>
			<FormControl size="small">
				<Button color="success" variant="contained" onClick={() => updateState({ createProject: true })} className="adminProjectsPage__addBtn btn-with-icon">
					<AddIcon /> { trans("allProjectsPage.addProject") }
				</Button>
			</FormControl>
		</div>
		<TableControls className="adminProjectsPage__filterArea">
			<AdminProjectsFilter config={state.filterData} onSetFilters={onSetFilters}>
				<div className="adminProjectsPage__filterCont">
					<FormControl size="small">
						<Button color="success" variant="contained" onClick={toggleFilters} className="adminProjectsPage__preBtn btn-with-icon">
							{ state.filterData ? <CloseIcon /> : <SearchIcon /> } { state.filterData ? trans("allProjectsPage.closeFilters") : trans("allProjectsPage.editFilters") }
						</Button>
					</FormControl>
					{ hasFilters() && <FormControl size="small">
						<Button color="success" variant="contained" onClick={clearFilters} className="adminProjectsPage__preBtn btn-with-icon">
							<DeleteIcon /> { trans("allProjectsPage.clearFilters") }
						</Button>
					</FormControl> }
					<span>
						{ filterSearchProjectsDesc(stateFilter, state.projectsCount) }
					</span>
				</div>
			</AdminProjectsFilter>
		</TableControls>
		<AdminProjectsTable items={state.projects} filter={stateFilter} loading={state.loading} onSortUpdate={onTableSortUpdate} selectedId={selectedId} />
		{ !state.loading && <MyPagination offset={stateFilter.offset} perPage={PAGE_SIZE_ADMIN_PROJECTS} total={state.projectsCount} onPageChange={onPageChange} /> }
		{ state.errorMsg && <ErrorInfo msg={state.errorMsg} className="margin-top-large" /> }
		{ state.createProject && <AdminCreateProjectModal onClose={() => updateState({ createProject: false, isDirty: false })}
			onSave={onProjectCreate} onChange={isDirty => updateState({ isDirty })} /> }
		{ state.bulkEmail && <AdminSendBulkEmailModal projectIds={state.projectIds} onClose={() => updateState({ bulkEmail: false, isDirty: false })} onChange={isDirty => updateState({ isDirty })} /> }
	</Page>;
}
